import React, { useState, useEffect } from 'react';
import { Modal, Input, InputNumber, Button, Checkbox, Switch, Space, message, Alert, Divider, Typography } from 'antd';
import { PlusOutlined, DeleteOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { adminAPI } from '../services/api';

const { Text } = Typography;

// 星期选项：0=周日 .. 6=周六
const WEEKDAY_OPTIONS = [
  { label: '周一', value: 1 }, { label: '周二', value: 2 }, { label: '周三', value: 3 },
  { label: '周四', value: 4 }, { label: '周五', value: 5 }, { label: '周六', value: 6 }, { label: '周日', value: 0 },
];

// 解析 metadata 中的 pricing_rules（容错）
export const parsePricingRules = (metadata) => {
  try {
    const meta = typeof metadata === 'string' ? JSON.parse(metadata || '{}') : (metadata || {});
    return Array.isArray(meta.pricing_rules) ? meta.pricing_rules : [];
  } catch (e) {
    return [];
  }
};

const emptyRule = () => ({
  label: '', priority: 10, weekdays: [1, 2, 3, 4, 5], start_time: '00:00', end_time: '24:00',
  holiday_exclude: false,
  min_input_tokens: null, max_input_tokens: null, min_output_tokens: null, max_output_tokens: null,
  input_price: null, cache_hit_price: null, cache_write_price: null, output_price: null,
});

// DeepSeek 官方峰谷模板（元/1K tokens，可按模型档位替换）
const deepSeekTemplate = (isPro) => {
  const p = isPro
    ? { peak: { in: 0.009, hit: 0.0003, out: 0.027 }, off: { in: 0.0045, hit: 0.00015, out: 0.0135 } }
    : { peak: { in: 0.003, hit: 0.0001, out: 0.009 }, off: { in: 0.0015, hit: 0.00005, out: 0.0045 } };
  return [
    { ...emptyRule(), label: '高峰', priority: 1, start_time: '09:00', end_time: '12:00', holiday_exclude: true, input_price: p.peak.in, cache_hit_price: p.peak.hit, output_price: p.peak.out },
    { ...emptyRule(), label: '高峰', priority: 1, start_time: '14:00', end_time: '18:00', holiday_exclude: true, input_price: p.peak.in, cache_hit_price: p.peak.hit, output_price: p.peak.out },
    { ...emptyRule(), label: '空闲', priority: 10, weekdays: [0, 1, 2, 3, 4, 5, 6], start_time: '00:00', end_time: '24:00', holiday_exclude: false, input_price: p.off.in, cache_hit_price: p.off.hit, output_price: p.off.out },
  ];
};

const HHMM_RE = /^([01]\d|2[0-4]):[0-5]\d$/; // 支持 24:00

const PriceInput = ({ value, onChange, placeholder = '继承基础价' }) => (
  <InputNumber
    value={value}
    onChange={onChange}
    min={0}
    step={0.0001}
    style={{ width: 96 }}
    placeholder={placeholder}
    size="small"
  />
);

const TokensInput = ({ value, onChange, placeholder = '不限' }) => (
  <InputNumber
    value={value}
    onChange={onChange}
    min={0}
    step={1}
    style={{ width: 76 }}
    placeholder={placeholder}
    size="small"
  />
);

/**
 * 定价规则编辑弹窗：分时价 / 缓存价 / 阶梯量价统一规则列表
 * 保存时合并进 model_configs.metadata.pricing_rules（复用 UpdateModel 的 metadata 白名单）
 */
const PricingRulesModal = ({ visible, model, onClose, onSaved }) => {
  const [rules, setRules] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (visible && model) {
      setRules(parsePricingRules(model.metadata));
    }
  }, [visible, model]);

  const updateRule = (idx, field, value) =>
    setRules(prev => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  const addRule = () => setRules(prev => [...prev, emptyRule()]);
  const removeRule = (idx) => setRules(prev => prev.filter((_, i) => i !== idx));

  const applyTemplate = () => {
    const isPro = /pro/i.test(model?.model_name || '');
    setRules(deepSeekTemplate(isPro));
    message.success(isPro ? '已填入 DeepSeek V4-Pro 峰谷模板' : '已填入 DeepSeek V4-Flash 峰谷模板');
  };

  const handleSave = async () => {
    for (const r of rules) {
      if (!(r.label || '').trim()) { message.warning('每条规则需要填写名称'); return; }
      if (!HHMM_RE.test(r.start_time) || !HHMM_RE.test(r.end_time)) { message.warning('时间格式需为 HH:MM（结束可 24:00）'); return; }
      if (r.end_time !== '24:00' && r.end_time <= r.start_time) { message.warning('结束时间需晚于开始时间'); return; }
      if ((r.weekdays || []).length === 0) { message.warning('请选择生效星期（或全选）'); return; }
    }
    setSaving(true);
    try {
      let meta = {};
      try { meta = typeof model.metadata === 'string' ? JSON.parse(model.metadata || '{}') : (model.metadata || {}); } catch (e) { meta = {}; }
      meta.pricing_rules = rules;
      await adminAPI.updateModelConfig(model.id, { metadata: JSON.stringify(meta) });
      message.success('定价规则已保存');
      try { await adminAPI.refreshGatewayModels(); } catch (e) { /* 刷新失败不阻断 */ }
      if (onSaved) onSaved();
      if (onClose) onClose();
    } catch (e) {
      message.error('保存失败: ' + (e?.response?.data?.message || e?.message || '未知错误'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`定价规则配置 - ${model?.model_name || ''}`}
      open={visible}
      onCancel={onClose}
      width={980}
      footer={[
        <Button key="template" icon={<ThunderboltOutlined />} onClick={applyTemplate}>填入 DeepSeek 峰谷模板</Button>,
        <Button key="cancel" onClick={onClose}>取消</Button>,
        <Button key="save" type="primary" loading={saving} onClick={handleSave}>保存规则</Button>,
      ]}
    >
      <Alert type="info" showIcon style={{ marginBottom: 12 }}
        message="规则说明：按优先级从小到大匹配第一条命中的规则。价格为元/1K tokens，留空=继承模型基础价。条件字段（星期/时间段/节假日排除/token 区间）为可选维度，全部留默认即全天生效。"
      />
      {rules.length === 0 && (
        <div style={{ textAlign: 'center', padding: 24, color: '#999' }}>
          暂无规则，可点击下方「新增规则」或「填入 DeepSeek 峰谷模板」
        </div>
      )}
      {rules.map((r, idx) => (
        <div key={idx} style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 12, marginBottom: 12, background: '#fafafa' }}>
          <Space wrap size={8} style={{ marginBottom: 8 }}>
            <Text strong>#{idx + 1}</Text>
            <Input size="small" style={{ width: 110 }} placeholder="名称，如：高峰"
              value={r.label} onChange={e => updateRule(idx, 'label', e.target.value)} />
            <span>优先级</span>
            <InputNumber size="small" min={0} max={99} style={{ width: 60 }}
              value={r.priority} onChange={v => updateRule(idx, 'priority', v)} />
            <span>时段</span>
            <Input size="small" style={{ width: 56 }} placeholder="HH:MM"
              value={r.start_time} onChange={e => updateRule(idx, 'start_time', e.target.value)} />
            <span>~</span>
            <Input size="small" style={{ width: 56 }} placeholder="24:00"
              value={r.end_time} onChange={e => updateRule(idx, 'end_time', e.target.value)} />
            <span>节假日排除</span>
            <Switch size="small" checked={!!r.holiday_exclude} onChange={v => updateRule(idx, 'holiday_exclude', v)} />
            <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => removeRule(idx)} />
          </Space>
          <div style={{ marginBottom: 8 }}>
            <Text type="secondary" style={{ marginRight: 8 }}>生效星期</Text>
            <Checkbox.Group options={WEEKDAY_OPTIONS} value={r.weekdays}
              onChange={v => updateRule(idx, 'weekdays', v)} />
          </div>
          <Space wrap size={8}>
            <span>输入tokens区间</span>
            <TokensInput value={r.min_input_tokens} onChange={v => updateRule(idx, 'min_input_tokens', v)} placeholder="不限" />
            <span>~</span>
            <TokensInput value={r.max_input_tokens} onChange={v => updateRule(idx, 'max_input_tokens', v)} placeholder="不限" />
            <span>输出tokens区间</span>
            <TokensInput value={r.min_output_tokens} onChange={v => updateRule(idx, 'min_output_tokens', v)} placeholder="不限" />
            <span>~</span>
            <TokensInput value={r.max_output_tokens} onChange={v => updateRule(idx, 'max_output_tokens', v)} placeholder="不限" />
          </Space>
          <Divider style={{ margin: '8px 0' }} />
          <Space wrap size={8}>
            <span>输入价(未命中)</span>
            <PriceInput value={r.input_price} onChange={v => updateRule(idx, 'input_price', v)} />
            <span>缓存命中价</span>
            <PriceInput value={r.cache_hit_price} onChange={v => updateRule(idx, 'cache_hit_price', v)} />
            <span>缓存创建价</span>
            <PriceInput value={r.cache_write_price} onChange={v => updateRule(idx, 'cache_write_price', v)} />
            <span>输出价</span>
            <PriceInput value={r.output_price} onChange={v => updateRule(idx, 'output_price', v)} />
          </Space>
        </div>
      ))}
      <Button type="dashed" block icon={<PlusOutlined />} onClick={addRule}>新增规则</Button>
    </Modal>
  );
};

export default PricingRulesModal;
